import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import Switch from 'ext-ui/components/Switch/Switch'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'

import SettingsControlRow from '../SettingsControlRow/SettingsControlRow'
import SettingsDisclosure from '../SettingsDisclosure/SettingsDisclosure'
import SettingsSegmented from '../SettingsSegmented/SettingsSegmented'
import SettingsTile from '../SettingsTile/SettingsTile'
import style from './SpendRestrictionsTile.style'

const PILL_CAP = 4

// Placeholder catalogs — production sources these from the processor / account.
const MERCHANT_CATEGORIES = [
  'Travel & lodging',
  'Restaurants & dining',
  'Office & software',
  'Advertising & marketing',
  'Entertainment',
  'Gambling',
  'Cash advance / ATM',
]

const GEOFENCE_COUNTRIES = [
  'United States',
  'Canada',
  'United Kingdom',
  'France',
  'Germany',
  'Australia',
  'Japan',
]

type MccMode = 'allowAllExcept' | 'blockAllExcept'

type ChannelKey = 'online' | 'swipe' | 'atm' | 'wallet'

function cappedPills(pills: ReactNode[]): ReactNode[] {
  if (pills.length <= PILL_CAP) {
    return pills
  }
  return [
    ...pills.slice(0, PILL_CAP),
    <Pill key="more" size="small">{`+${pills.length - PILL_CAP} more`}</Pill>,
  ]
}

function MerchantCategoriesDrawer(props: {
  open: boolean
  onClose: () => void
  mode: MccMode
  exceptions: string[]
  onMode: (mode: MccMode) => void
  onAdd: (name: string) => void
  onRemove: (name: string) => void
}): ReactElement {
  const { open, onClose, mode, exceptions, onMode, onAdd, onRemove } = props
  const translate = useTranslate()
  const [query, setQuery] = useState('')

  // The drawer stays mounted across open/close, so clear the search each time it
  // closes — otherwise it reopens pre-filtered to the previous query.
  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const addVerb =
    mode === 'allowAllExcept' ? translate('block') : translate('allow')
  const listLabel =
    mode === 'allowAllExcept' ? translate('Blocked') : translate('Allowed')
  const addable = MERCHANT_CATEGORIES.filter(
    (name) =>
      !exceptions.includes(name) &&
      name.toLowerCase().includes(query.trim().toLowerCase()),
  )

  return (
    <BaseFlyout
      isOpen={open}
      onClose={onClose}
      aria-label={translate('Merchant categories')}
      title={translate('Merchant categories')}
      subtitle={translate(
        'Choose where this card can be used. These controls are a preview — nothing is saved and no restriction is enforced yet.',
      )}
    >
      <div className="SpendRestrictionsTile-drawer" css={style}>
        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {translate('Mode')}
          </div>
          <SettingsSegmented
            ariaLabel={translate('Merchant-category default')}
            value={mode}
            options={[
              {
                label: translate('Allow all except'),
                value: 'allowAllExcept',
              },
              {
                label: translate('Block all except'),
                value: 'blockAllExcept',
              },
            ]}
            onChange={(next) => onMode(next as MccMode)}
          />
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {translate('Add a category to ${verb}', { verb: addVerb })}
          </div>
          <ExtUITextInput
            variant="small"
            label={translate('Search categories')}
            placeholder={translate('Type a category name…')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query.trim() !== '' && (
            <ul className="SpendRestrictionsTile-addList">
              {addable.length === 0 ? (
                <li className="SpendRestrictionsTile-addEmpty">
                  {translate('No matches')}
                </li>
              ) : (
                addable.map((name) => (
                  <li key={name}>
                    <button
                      type="button"
                      className="SpendRestrictionsTile-addRow"
                      onClick={() => {
                        onAdd(name)
                        setQuery('')
                      }}
                    >
                      <span>{name}</span>
                      <span aria-hidden="true">{`+ ${addVerb}`}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {exceptions.length > 0
              ? `${listLabel} · ${exceptions.length}`
              : listLabel}
          </div>
          {exceptions.length === 0 ? (
            <p className="SpendRestrictionsTile-drawerEmpty">
              {mode === 'allowAllExcept'
                ? translate('No exceptions — every category is allowed.')
                : translate('No exceptions — every category is blocked.')}
            </p>
          ) : (
            <ul className="SpendRestrictionsTile-exceptionList">
              {exceptions.map((name) => (
                <li key={name} className="SpendRestrictionsTile-exceptionRow">
                  <span className="SpendRestrictionsTile-exceptionName">
                    {name}
                  </span>
                  <span className="SpendRestrictionsTile-exceptionControl">
                    <Pill
                      size="small"
                      color={mode === 'allowAllExcept' ? 'severe' : 'highlight'}
                    >
                      {listLabel}
                    </Pill>
                    <button
                      type="button"
                      className="SpendRestrictionsTile-removeButton"
                      aria-label={translate('Remove ${name}', { name })}
                      onClick={() => onRemove(name)}
                    >
                      {'×'}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BaseFlyout>
  )
}

function GeofenceDrawer(props: {
  open: boolean
  onClose: () => void
  countries: string[]
  onAdd: (country: string) => void
  onRemove: (country: string) => void
}): ReactElement {
  const { open, onClose, countries, onAdd, onRemove } = props
  const translate = useTranslate()
  const [query, setQuery] = useState('')

  // Clear the search when the drawer closes so it reopens fresh (it stays
  // mounted across open/close).
  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const addable = GEOFENCE_COUNTRIES.filter(
    (country) =>
      !countries.includes(country) &&
      country.toLowerCase().includes(query.trim().toLowerCase()),
  )

  return (
    <BaseFlyout
      isOpen={open}
      onClose={onClose}
      aria-label={translate('Geofence')}
      title={translate('Geofence')}
      subtitle={translate(
        'Choose where this card can be used. These controls are a preview — nothing is saved and no restriction is enforced yet.',
      )}
    >
      <div className="SpendRestrictionsTile-drawer" css={style}>
        <div className="SpendRestrictionsTile-drawerSection">
          <ExtUITextInput
            variant="small"
            label={translate('Add a country')}
            placeholder={translate('Search countries…')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query.trim() !== '' && (
            <ul className="SpendRestrictionsTile-addList">
              {addable.length === 0 ? (
                <li className="SpendRestrictionsTile-addEmpty">
                  {translate('No matches')}
                </li>
              ) : (
                addable.map((country) => (
                  <li key={country}>
                    <button
                      type="button"
                      className="SpendRestrictionsTile-addRow"
                      onClick={() => {
                        onAdd(country)
                        setQuery('')
                      }}
                    >
                      <span>{country}</span>
                      <span aria-hidden="true">{translate('+ add')}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {countries.length > 0
              ? `${translate('Permitted')} · ${countries.length}`
              : translate('Permitted')}
          </div>
          {countries.length === 0 ? (
            <p className="SpendRestrictionsTile-drawerEmpty">
              {translate('No regions — the card is blocked worldwide.')}
            </p>
          ) : (
            <ul className="SpendRestrictionsTile-exceptionList">
              {countries.map((country) => (
                <li
                  key={country}
                  className="SpendRestrictionsTile-exceptionRow"
                >
                  <span className="SpendRestrictionsTile-exceptionName">
                    {country}
                  </span>
                  <button
                    type="button"
                    className="SpendRestrictionsTile-removeButton"
                    aria-label={translate('Remove ${name}', { name: country })}
                    onClick={() => onRemove(country)}
                  >
                    {'×'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BaseFlyout>
  )
}

/**
 * "Where & how it can spend" tile (EX-40354) — merchant categories, payment
 * channels, and geofence controls. Visual-only with placeholder data; edits are
 * held in local state and not persisted.
 */
export default function SpendRestrictionsTile(): ReactElement {
  const translate = useTranslate()

  const [mccMode, setMccMode] = useState<MccMode>('allowAllExcept')
  const [mccExceptions, setMccExceptions] = useState<string[]>([
    'Gambling',
    'Cash advance / ATM',
  ])
  const [mccDrawerOpen, setMccDrawerOpen] = useState(false)

  const [channels, setChannels] = useState<Record<ChannelKey, boolean>>({
    online: true,
    swipe: true,
    atm: false,
    wallet: true,
  })

  const [countries, setCountries] = useState<string[]>([
    'United States',
    'Canada',
  ])
  const [geofenceDrawerOpen, setGeofenceDrawerOpen] = useState(false)

  const channelRows: {
    key: ChannelKey
    label: string
    shortLabel: string
    description: string
  }[] = [
    {
      key: 'online',
      label: translate('Online & e-commerce'),
      shortLabel: translate('Online'),
      description: translate('Card-not-present purchases'),
    },
    {
      key: 'swipe',
      label: translate('In-store (swipe / tap)'),
      shortLabel: translate('In-store'),
      description: translate('Card-present terminals'),
    },
    {
      key: 'atm',
      label: translate('ATM withdrawals'),
      shortLabel: translate('ATM'),
      description: translate('Cash access'),
    },
    {
      key: 'wallet',
      label: translate('Mobile wallet'),
      shortLabel: translate('Mobile wallet'),
      description: translate('Apple Pay / Google Pay'),
    },
  ]

  const mccSummary =
    mccExceptions.length === 0 ? (
      <span className="SpendRestrictionsTile-summaryText">
        {mccMode === 'allowAllExcept'
          ? translate('All categories allowed')
          : translate('All categories blocked')}
      </span>
    ) : (
      <span className="SpendRestrictionsTile-summaryPills">
        {cappedPills(
          mccExceptions.map((name) => (
            <Pill
              key={name}
              size="small"
              color={mccMode === 'allowAllExcept' ? 'severe' : 'highlight'}
            >
              {name}
            </Pill>
          )),
        )}
      </span>
    )

  const channelSummary = (
    <span className="SpendRestrictionsTile-summaryPills">
      {cappedPills(
        [...channelRows]
          .sort((a, b) => Number(channels[b.key]) - Number(channels[a.key]))
          .map((row) => (
            <Pill
              key={row.key}
              size="small"
              color={channels[row.key] ? 'saas' : 'severe'}
            >
              {row.shortLabel}
            </Pill>
          )),
      )}
    </span>
  )

  const geofenceSummary =
    countries.length === 0 ? (
      <span className="SpendRestrictionsTile-summaryText">
        {translate('Worldwide blocked')}
      </span>
    ) : (
      <span className="SpendRestrictionsTile-summaryPills">
        {cappedPills(
          countries.map((country) => (
            <Pill key={country} size="small">
              {country}
            </Pill>
          )),
        )}
      </span>
    )

  const addMccException = (name: string): void =>
    setMccExceptions((prev) => (prev.includes(name) ? prev : [...prev, name]))
  const removeMccException = (name: string): void =>
    setMccExceptions((prev) => prev.filter((item) => item !== name))
  const addCountry = (country: string): void =>
    setCountries((prev) => (prev.includes(country) ? prev : [...prev, country]))
  const removeCountry = (country: string): void =>
    setCountries((prev) => prev.filter((item) => item !== country))

  return (
    <SettingsTile title={translate('Where & how it can spend')}>
      <div className="SpendRestrictionsTile" css={style}>
        <p className="SpendRestrictionsTile-intro">
          {translate(
            'Where and how this card can be used. These controls are a preview — edits stay on this screen, are not saved, and change nothing on the card. Tap a row to expand.',
          )}
        </p>

        <SettingsDisclosure
          title={translate('Merchant categories')}
          summary={mccSummary}
          actionLabel={translate('Edit')}
        >
          <p className="SpendRestrictionsTile-body">
            {mccMode === 'allowAllExcept'
              ? translate(
                  'Everything else is allowed. Edits here are not saved yet.',
                )
              : translate(
                  'Everything else is blocked. Edits here are not saved yet.',
                )}
          </p>
          <Button
            variant="secondary"
            dropShadow={false}
            onClick={() => setMccDrawerOpen(true)}
            data-testid="edit-merchant-categories"
          >
            {translate('Edit categories')}
          </Button>
        </SettingsDisclosure>

        <SettingsDisclosure
          title={translate('Payment channels')}
          summary={channelSummary}
          actionLabel={translate('Edit')}
        >
          <p className="SpendRestrictionsTile-body">
            {translate(
              'Disable a channel to shrink the fraud surface. Turned-off channels decline in real time.',
            )}
          </p>
          {channelRows.map((row) => (
            <SettingsControlRow
              key={row.key}
              title={row.label}
              description={row.description}
            >
              <Switch
                checked={channels[row.key]}
                ariaLabel={row.label}
                onCheckedChange={(checked) =>
                  setChannels((prev) => ({ ...prev, [row.key]: checked }))
                }
                data-testid={`channel-${row.key}`}
              />
            </SettingsControlRow>
          ))}
        </SettingsDisclosure>

        <SettingsDisclosure
          title={translate('Geofence')}
          summary={geofenceSummary}
          actionLabel={translate('Edit')}
        >
          <p className="SpendRestrictionsTile-body">
            {translate(
              'Approvals outside the permitted regions are declined. All other countries are blocked.',
            )}
          </p>
          <div className="SpendRestrictionsTile-expandedPills">
            {geofenceSummary}
          </div>
          <Button
            variant="secondary"
            dropShadow={false}
            onClick={() => setGeofenceDrawerOpen(true)}
            data-testid="edit-geofence"
          >
            {translate('Edit countries')}
          </Button>
        </SettingsDisclosure>
      </div>

      <MerchantCategoriesDrawer
        open={mccDrawerOpen}
        onClose={() => setMccDrawerOpen(false)}
        mode={mccMode}
        exceptions={mccExceptions}
        onMode={setMccMode}
        onAdd={addMccException}
        onRemove={removeMccException}
      />
      <GeofenceDrawer
        open={geofenceDrawerOpen}
        onClose={() => setGeofenceDrawerOpen(false)}
        countries={countries}
        onAdd={addCountry}
        onRemove={removeCountry}
      />
    </SettingsTile>
  )
}
